import { AccessRightModel, MatchingModel } from "@/Domains";

import { NeedModel } from '../NeedModel';
import {
    AppEnumHelper,
    DateFormatHelper,
    DetailItem,
    DetailSection,
    DOMAIN_TYPES,
    formatName,
    NeedEnums,
    SoNumber,
    SortModelHelper
} from "@/Shared";

import { AuditViewModel } from "@/Shared/Logic/ViewModels/AuditViewModel";
import { AuditModel } from "@/Shared/Logic/Models/AuditModel";
import { Row } from "@/Shared/UI/Components/DetailsListBox/DetailsListBox";

/**
 * =====================================================
 * Parameters for NeedViewModel
 * =====================================================
 *
 * Encapsulates all information required to build the Need view model:
 * - enriched Need entity
 * - enums for display labels
 * - access rights
 * - current display state (active/inactive)
 */
export interface NeedVMParams {
    need: NeedModel;
    viewEnums: NeedEnums;
    isDisplaying: boolean;
    accessRight: AccessRightModel
}

/**
 * =====================================================
 * NeedViewModel
 * =====================================================
 *
 * Responsible for:
 * - Mapping Need entity fields to UI-friendly values
 * - Generating detail sections for detail views
 * - Handling display logic for status, card class, and title
 * - Formatting dates, numbers, and labels using helpers
 * - Providing comparison method for sorting
 *
 * Author: Samano CASTRE
* Date: July 17, 2025
 */
export class NeedViewModel {

    public id!: number;
    public model!: NeedModel;
    public operationType!: string;
    public propertyCategory!: string;
    public propertyType!: string;
    public sectorName!: string;
    public cityName!: string;
    public furnished!: string;
    public surfaceArea!: string;
    public standing!: string;

    public desiredStartAt!: string;

    public priceRangeLabel!: string;
    public contactName!: string;
    public matchings: MatchingModel[] = [];
    public createdAt!: string;
    public status!: string;
    public statusClass!: string;
    public cardHeaderTitle!: string;
    public audit!: AuditViewModel;
    public cardClass!: string;
    public isDeleted!: boolean;

    public maxServiceChargeProvision!: string;

    public propertyUsageType!: string;


    /** Constructor – initializes the view model from parameters */
    public constructor(protected params: NeedVMParams) {
        this.initialize();
    }

    /**
     * =====================================================
     * Initialization
     * =====================================================
     *
     * Maps the Need entity and enums to view-friendly fields.
     * Formats dates, numbers, and labels. Sets card and status classes.
     */
    private initialize() {
        const { need, viewEnums, isDisplaying } = this.params;

        this.audit = new AuditViewModel(need as AuditModel);
        this.model = need;
        this.operationType = AppEnumHelper.getLabel(viewEnums.OperationTypeEnum, need.operationType);

        this.standing = AppEnumHelper.getLabel(viewEnums.StandingEnum, need.propertyStanding);

        this.propertyType = AppEnumHelper.getLabel(viewEnums.PropertyTypeEnum, need.propertyType);

        this.propertyCategory = AppEnumHelper.getLabel(viewEnums.PropertyCategoryEnum, need.propertyCategory);

        this.id = need?.id || 0;

        this.matchings = need.matchings || [];

        this.sectorName = need?.sector?.name || "";

        this.cityName = need?.sector?.city?.name || "";

        this.contactName = formatName(need.contact?.person);

        this.furnished = need.furnished ? "Oui" : "Non";

        this.surfaceArea = String(SoNumber.toFR(need.surfaceArea));

        this.priceRangeLabel = AppEnumHelper.getLabel(viewEnums.PriceRangeEnum, need.priceRange);

        this.isDeleted = !!need.deletedAt;

        this.createdAt = DateFormatHelper.toLocaleString(need.createdAt);

        this.desiredStartAt = DateFormatHelper.toLocaleDateString(need.desiredStartAt);

        this.maxServiceChargeProvision = SoNumber.formatAmount(need.maxServiceChargeProvision);

        this.status = AppEnumHelper.getLabel(this.params.viewEnums.NeedStatusEnum, this.model.status);

        this.initClassByStatus();

        this.cardHeaderTitle = this.propertyType + " - " + this.cityName;
        this.cardClass = isDisplaying ? 'active' : '';

        this.propertyUsageType = AppEnumHelper.getLabel(viewEnums.PropertyUsageEnum, this.model?.usageType);

    }

    /**
     * =====================================================
     * Status Class
     * =====================================================
     *
     * Computes CSS class based on the current status enum.
     */
    private initClassByStatus() {
        const statusEnum = AppEnumHelper.getByValue(this.params.viewEnums.NeedStatusEnum, this.model.status);
        this.statusClass = "need-status--" + (statusEnum?.key ?? "").toUpperCase();
    }


    /**
     * =====================================================
     * Detail Sections
     * =====================================================
     *
     * Builds sections for the detail view:
     * - General info
     * - Need-specific fields
     * - Matchings
     * - Audit trail (if access permitted)
     */
    getAllSections = (): DetailSection[] => {
        const sections: DetailSection[] = [
            {
                sectionFn: this.getGlobalSection,
                legend: 'Détails du besoin',
                type: 'box'
            },
            {
                sectionFn: this.getNeedSection,
                legend: 'Biens recherchés',
                type: 'box'
            },
            {
                sectionFn: this.getMatchingSection,
                legend: 'Rapprochements',
                type: 'list'
            }
        ];

        if (this.params.accessRight.canAudit(DOMAIN_TYPES.Need)) {
            sections.push(
                {
                    sectionFn: this.audit.getAuditTrail,
                    legend: 'Traçabilité',
                    type: 'box'
                },
            );
        }
        return sections;

    }

    /** Generates the title of the Need card */
    get title() {
        let title = "Besoin N° " + this.id;
        if (this.propertyCategory.length > 0) {
            title += ' - ' + this.propertyCategory;
        }

        if (this.propertyType.length > 0) {
            title += ' | ' + this.propertyType;
        }
        return title;
    }

    /** =====================================================
     * Section builders
     * ===================================================== */

    getGlobalSection = () => {
        return [
            { label: "N° Besoin", value: this.id },
            { label: "Prospect", value: this.contactName },
            { label: "Type d'opération", value: this.operationType },
            { label: "Budget", value: this.priceRangeLabel },
            { label: "Charges souhaitées", value: this.maxServiceChargeProvision },
            { label: "Statut", value: this.status },
            { label: "Date souhaitée", value: this.desiredStartAt },
            { label: "Date création", value: this.createdAt },
        ] as DetailItem[];
    }

    getNeedSection = () => {
        return [
            { label: "Nature du bien", value: this.propertyCategory },
            { label: "Type du bien", value: this.propertyType },
            { label: "Usage", value: this.propertyUsageType },
            { label: "Standing", value: this.standing },
            { label: "Meublé", value: this.furnished },
            { label: "Surface terrain", value: this.surfaceArea },
            { label: "Ville", value: this.cityName },
            { label: "Secteur", value: this.sectorName },
        ] as DetailItem[];
    }

    getMatchingSection = () => {
        const headers = ["N° Appr.", "N° Bien", "Status", "Date"];

        const rows = [] as Row[];
        this.matchings.forEach(matching =>
            rows.push(
                {
                    items: [
                        { label: String(matching.id) },
                        { label: String(matching.propertyId) },
                        { label: AppEnumHelper.getLabel(this.params.viewEnums.MatchingStatusEnum, matching.status) },
                        { label: DateFormatHelper.toLocaleString(matching.createdAt) || "" }
                    ]
                })
        );

        return { headers, rows };
    }

    /** =====================================================
    * Sorting helper
    * =====================================================
    *
    * Compares two NeedViewModels based on status enum.
    */
    compareTo(other: NeedViewModel): number {
        return SortModelHelper.sortNeed(this.params.viewEnums.NeedStatusEnum, this.model, other.model);
    }
}